"use client";
import { useState } from "react";
import {
  CloseButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@stadium/ui/src/components/ModalNew/utils";

import Button from "@stadium/checkout/src/components/shared/buttons";
import AddressView from "@stadium/checkout/src/components/shipping-address/address-confirmation/view";

import { createAddressRequestParams } from "@stadium/checkout/src/helpers/address";

import styles from "../address.module.scss";

const modalContent = {
  valid: {
    heading: "Confirm your address",
    body: "To make sure you receive your order on time, we suggest using the following shipping address:",
  },
  invalid: {
    heading: "Address not verified",
    body: "We were not able to find and verify your shipping address. Please certify that your address is correct.",
  },
};

const FormCta = ({ onClick, children, ...rest }) => (
  <Button styleType="primary" onClick={onClick} {...rest}>
    {children}
  </Button>
);

const AddressConfirmation = (props) => {
  const {
    type = "valid",
    invalidAddress,
    validAddress,
    isInternational,
    onAddAddress,
    setFormErrors,
    countryId,
    countryName = "",
    closeModal,
    modalHeaderTitleClassName = "",
    iconCloseClass = "",
    modalBodyClass = "",
    modalFooterClass = "",
    parentClassName = false,
    handleValidateFormError,
  } = props;

  const [isSubmittingInvalidAddress, setIsSubmittingInvalidAddress] = useState(false);
  const [isSubmittingValidAddress, setIsSubmittingValidAddress] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const renderActionButtons = () => {
    switch (type) {
      case "valid":
        return renderValidActionButtons();

      case "invalid":
        return renderInvalidActionButtons();

      default:
        return "";
    }
  };

  const submitUnverifiedAddress = () => {
    setIsSubmittingInvalidAddress(true);
    handleSubmit(invalidAddress);
  };

  const submitVerifiedAddress = () => {
    setIsSubmittingValidAddress(true);
    handleSubmit(validAddress);
  };

  const handleSubmit = (addressFormValues) => {
    setIsSubmitting(true);
    const params = createAddressRequestParams({ ...addressFormValues, countryId });
    const onError = (errors) => {
      closeModal();
      if (setFormErrors) {
        setFormErrors(errors);
      }
      if (handleValidateFormError) {
        handleValidateFormError(errors);
      }
    };
    return onAddAddress(params, onError);
  };

  const renderUnverifiedSubmitCta = ({ msg = "Use Existing Address" }) => (
    <Button
      isLoading={isSubmittingInvalidAddress}
      onClick={() => !isSubmitting && submitUnverifiedAddress()}
      styleType="primary-outline"
    >
      {msg}
    </Button>
  );

  const renderValidActionButtons = () => (
    <>
      {renderUnverifiedSubmitCta({})}
      <Button
        isLoading={isSubmittingValidAddress}
        onClick={() => !isSubmitting && submitVerifiedAddress()}
        styleType="primary"
      >
        Accept Address
      </Button>
    </>
  );

  const renderInvalidActionButtons = () => (
    <>
      {renderUnverifiedSubmitCta({ msg: "Address is correct" })}
      <FormCta onClick={closeModal}>
        <span>Edit Address</span>
      </FormCta>
    </>
  );

  const renderAddress = () => {
    const addressToRender = type === "valid" ? { ...validAddress } : { ...invalidAddress };
    addressToRender.stateAbbreviation = addressToRender.stateName;
    if (countryName) addressToRender.countryName = countryName;
    return <AddressView address={addressToRender} isInternational={isInternational} />;
  };

  const renderBadge = () => {
    const badgeText = type === "valid" ? "SUGGESTED FOR DELIVERY" : "UNVERIFIED";

    return (
      <div className={styles.badgeCol}>
        <span className={styles.badgeDanger}>
          <small className={styles.badgeText}>{badgeText}</small>
        </span>
      </div>
    );
  };

  return (
    <div className={parentClassName ? styles.editModalCopy : ""}>
      <ModalHeader className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={`${styles.modalHeading} ${modalHeaderTitleClassName}`}>
            {modalContent[type]?.heading}
          </div>
        </div>
        <CloseButton onClick={closeModal} className={`${styles.closeIcon} ${iconCloseClass}`} />
      </ModalHeader>
      <ModalBody className={`${styles.modalBody} ${modalBodyClass}`}>
        <div className={styles.modalSubHeading}>{modalContent[type]?.body}</div>
        <div className={styles.mdInfo}>
          <div className={styles.mdRow}>
            <div className={styles.mdAddressCol}>{renderAddress()}</div>
            <div className={styles.mdBadgeCol}> {renderBadge()}</div>
          </div>
        </div>
      </ModalBody>
      <ModalFooter className={`${styles.modalFooter} ${modalFooterClass}`}>
        {renderActionButtons()}
      </ModalFooter>
    </div>
  );
};

export default AddressConfirmation;
